// <AiModule/> — THE AI module. One component, mounted on every screen that
// has an AI presence (SyQi's home overview, every tool's deity panel).
// Replaces the old per-screen bespoke panels (SyqiOverview text mode,
// SyqiOverviewVideo, SyqiOverviewVoice, DeityInterface's own layout).
//
// Anatomy (Joel's 2026-07-26 spec sketch — letters are his):
//   I  master toggle, above the card. Off ⇒ the module renders nothing but
//      the toggle itself.
//   A  play/pause, bottom LEFT of the video stage.
//   B  ⋮ menu, top right of the stage. Holds "Video" on/off — with video off
//      the stage collapses to a slim audio read-out bar — plus any extra
//      per-screen items the host passes in.
//   G  the ONE shared text space. Every view (daily overview, main points,
//      suggestions, chat, …) renders here; they no longer each own an
//      accordion.
//   D  chevron, bottom left — accordion that hides/shows G.
//   E  dropdown, picks WHICH view is showing in G.
//   F  the user's text input.
//   H  send.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Easing, Pressable, ScrollView, StyleSheet, Switch, Text, TextInput, View } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { DISPLAY_FONT, makeThemedStyles, radii, useTheme } from '../v10/theme';
import { GlassCard } from '../v10/ui';
import { VideoAvatar } from './VideoAvatar';
import type { VideoAvatarHandle } from './videoAvatarTypes';
import { useAiVoice, type VoiceTrack } from './useAiVoice';
import type { ChatMessage } from './useSyqiChat';

/** One entry in the E dropdown. `key === 'chat'` is reserved for the live
 *  conversation view, which the module renders itself. */
export interface AiModuleView {
  key: string;
  label: string;
  /** Body for the shared text space. Omitted for the chat view. */
  render?: () => React.ReactNode;
  /** What the play button reads out while this view is selected, when the
   *  module has no pre-rendered track. */
  speakText?: string;
}

export interface AiModuleMenuItem {
  label: string;
  onPress: () => void;
  disabled?: boolean;
}

export interface AiModuleProps {
  /** Namespaces this module's persisted UI state. e.g. 'home' or `tool.krnos`. */
  stateKey: string;
  /** Shown next to the master toggle. */
  title: string;
  /** Master toggle's initial value before AsyncStorage resolves. */
  defaultEnabled?: boolean;
  /** Accent for the master toggle (a deity's gear color, say). */
  accentColor?: string;
  /**
   * Renders the master-toggle row as a full-bleed navy bar that reads as one
   * continuous band with the shell header above it. Opt-in: only the SyQi
   * home module uses it; deity modules keep the plain muted label.
   */
  navyLabelBar?: boolean;

  views: AiModuleView[];
  /** Extra rows in the ⋮ menu, below the video toggle. */
  menuItems?: AiModuleMenuItem[];

  /** Chat plumbing — normally passed straight through from useSyqiChat. */
  messages: ChatMessage[];
  sending: boolean;
  loadingMessages?: boolean;
  onSend: (text: string) => void | Promise<unknown>;
  placeholder?: string;
  /** Rendered at the end of the chat stream (proposal cards, write receipts). */
  chatExtras?: React.ReactNode;
  /** Empty-state line for the chat view. */
  emptyChatText?: string;

  /** GLB for the video stage. Omit for SyQi's default avatar. */
  glbUrl?: string;
  /** Pre-rendered seekable audio for the primary script, when one exists. */
  track?: VoiceTrack | null;
  trackLoading?: boolean;
  /** Speak each new assistant reply as it lands. Default true. */
  speakReplies?: boolean;
  /** Rendered behind the card (e.g. <CloudBackdrop/> on SyQi's home). */
  backdrop?: React.ReactNode;
  /** Banner above the text space — loading skeletons, error lines. */
  banner?: React.ReactNode;
}

/** Animated 4-bar equalizer shown while TTS audio is being generated. */
function VoicePulse() {
  const styles = useStyles();
  const bars = useRef([0, 1, 2, 3].map(() => new Animated.Value(0.35))).current;
  useEffect(() => {
    const loops = bars.map((v, i) =>
      Animated.loop(
        Animated.sequence([
          Animated.delay(i * 110),
          Animated.timing(v, { toValue: 1, duration: 320, easing: Easing.inOut(Easing.ease), useNativeDriver: false }),
          Animated.timing(v, { toValue: 0.35, duration: 320, easing: Easing.inOut(Easing.ease), useNativeDriver: false }),
        ])
      )
    );
    loops.forEach((l) => l.start());
    return () => loops.forEach((l) => l.stop());
  }, [bars]);

  return (
    <View style={styles.voicePulseRow}>
      {bars.map((v, i) => (
        <Animated.View
          key={i}
          style={[styles.voicePulseBar, { height: v.interpolate({ inputRange: [0, 1], outputRange: [6, 26] }) }]}
        />
      ))}
    </View>
  );
}

function fmtTime(sec: number) {
  const m = Math.floor(sec / 60);
  const ss = Math.floor(sec % 60);
  return `${m}:${ss < 10 ? '0' : ''}${ss}`;
}

export function AiModule({
  stateKey,
  title,
  defaultEnabled = true,
  accentColor,
  navyLabelBar,
  views,
  menuItems,
  messages,
  sending,
  loadingMessages,
  onSend,
  placeholder = 'Ask SyQi…',
  chatExtras,
  emptyChatText,
  glbUrl,
  track,
  trackLoading,
  speakReplies = true,
  backdrop,
  banner,
}: AiModuleProps) {
  const { t } = useTheme();
  const styles = useStyles();
  const ns = `ai.module.${stateKey}`;

  const [enabled, setEnabled] = useState(defaultEnabled);
  const [videoOn, setVideoOn] = useState(true);
  const [chatOpen, setChatOpen] = useState(true);
  const [viewKey, setViewKey] = useState(views[0]?.key ?? 'chat');
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const [input, setInput] = useState('');
  const [avatarError, setAvatarError] = useState<string | null>(null);

  const avatarRef = useRef<VideoAvatarHandle>(null);
  const scrollRef = useRef<ScrollView>(null);
  const barWidthRef = useRef(1);

  // Persisted UI state. Written on every change, read once on mount.
  useEffect(() => {
    let cancelled = false;
    AsyncStorage.multiGet([`${ns}.enabled`, `${ns}.video`, `${ns}.chatOpen`, `${ns}.view`]).then((pairs) => {
      if (cancelled) return;
      const map = Object.fromEntries(pairs) as Record<string, string | null>;
      if (map[`${ns}.enabled`] != null) setEnabled(map[`${ns}.enabled`] === '1');
      if (map[`${ns}.video`] != null) setVideoOn(map[`${ns}.video`] === '1');
      if (map[`${ns}.chatOpen`] != null) setChatOpen(map[`${ns}.chatOpen`] === '1');
      const savedView = map[`${ns}.view`];
      if (savedView && views.some((v) => v.key === savedView)) setViewKey(savedView);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ns]);

  const persist = useCallback((suffix: string, value: string) => {
    AsyncStorage.setItem(`${ns}.${suffix}`, value).catch(() => {});
  }, [ns]);

  const voice = useAiVoice({ avatarRef: videoOn ? avatarRef : undefined, track });
  const { speak, togglePlay, seek, playing, speaking, ttsLoading, ttsError, position, duration } = voice;

  const activeView = views.find((v) => v.key === viewKey) ?? views[0];

  // Speak each NEW assistant reply as it lands — not the history that loads
  // in when the conversation is first opened.
  const trackingRef = useRef<{ started: boolean; count: number }>({ started: false, count: 0 });
  useEffect(() => {
    if (!enabled || !speakReplies) return;
    if (loadingMessages) {
      trackingRef.current.started = false;
      return;
    }
    if (!trackingRef.current.started) {
      trackingRef.current.started = true;
      trackingRef.current.count = messages.length;
      return;
    }
    if (messages.length > trackingRef.current.count) {
      const last = messages[messages.length - 1];
      if (last.role === 'assistant' && !last.isError) speak(last.content);
    }
    trackingRef.current.count = messages.length;
  }, [messages, loadingMessages, speak, enabled, speakReplies]);

  const lastAssistant = useMemo(
    () => [...messages].reverse().find((m) => m.role === 'assistant' && !m.isError)?.content,
    [messages]
  );

  const onPlayPause = () => togglePlay(activeView?.speakText ?? lastAssistant);

  const submit = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setViewKey('chat');
    persist('view', 'chat');
    if (!chatOpen) {
      setChatOpen(true);
      persist('chatOpen', '1');
    }
    await onSend(text);
    requestAnimationFrame(() => scrollRef.current?.scrollToEnd({ animated: true }));
  };

  const toggleEnabled = (next: boolean) => {
    setEnabled(next);
    persist('enabled', next ? '1' : '0');
  };

  const toggleVideo = () => {
    const next = !videoOn;
    setVideoOn(next);
    persist('video', next ? '1' : '0');
    setMenuOpen(false);
  };

  const toggleChat = () => {
    const next = !chatOpen;
    setChatOpen(next);
    persist('chatOpen', next ? '1' : '0');
  };

  const pickView = (key: string) => {
    setViewKey(key);
    persist('view', key);
    setDropOpen(false);
    if (!chatOpen) {
      setChatOpen(true);
      persist('chatOpen', '1');
    }
  };

  // ── I: master toggle ─────────────────────────────────────────────────────
  const toggleRow = (
    <View style={[styles.masterRow, navyLabelBar && styles.masterRowNavy]}>
      <Text style={[styles.masterLabel, navyLabelBar && styles.masterLabelNavy]}>{title}</Text>
      <Switch
        value={enabled}
        onValueChange={toggleEnabled}
        trackColor={{ true: t.goldGlow, false: t.navyLine }}
        thumbColor={enabled ? accentColor ?? t.gold : t.ivorySoft}
        ios_backgroundColor={t.navyLine}
      />
    </View>
  );

  if (!enabled) return toggleRow;

  // ── B: the ⋮ menu ────────────────────────────────────────────────────────
  const dotStyle = videoOn ? styles.dotOnVideo : styles.dot;
  const dots = (
    <Pressable style={styles.dotsButton} onPress={() => setMenuOpen((v) => !v)} hitSlop={8}>
      <View style={dotStyle} />
      <View style={dotStyle} />
      <View style={dotStyle} />
    </Pressable>
  );

  // ── A: play/pause. Floats bottom-left over the stage; sits inline as the
  //      first cell of the read-out bar when video is off.
  const renderPlay = (floating: boolean) => (
    <Pressable
      style={[styles.playButton, floating ? styles.playButtonFloating : styles.playButtonInline]}
      onPress={onPlayPause}
      disabled={trackLoading && !track}
    >
      <Text style={styles.playButtonText}>{playing ? '❚❚' : '▶'}</Text>
    </Pressable>
  );

  const renderScrubber = (floating: boolean) =>
    track && duration > 0 ? (
      <View style={[styles.trackRow, floating ? styles.trackRowFloating : styles.trackRowInline]}>
        <Text style={[styles.trackTime, !floating && styles.trackTimeDark]}>{fmtTime(position)}</Text>
        <Pressable
          style={styles.trackBar}
          onLayout={(e) => {
            barWidthRef.current = Math.max(1, e.nativeEvent.layout.width);
          }}
          onPress={(e) => seek(e.nativeEvent.locationX / barWidthRef.current)}
        >
          <View style={[styles.trackRail, !floating && styles.trackRailDark]}>
            <View style={[styles.trackFill, { width: `${Math.min(100, (position / duration) * 100)}%` }]} />
          </View>
        </Pressable>
        <Text style={[styles.trackTime, !floating && styles.trackTimeDark]}>{fmtTime(duration)}</Text>
      </View>
    ) : null;

  return (
    <View style={styles.wrap}>
      {toggleRow}

      <GlassCard style={styles.card}>
        {backdrop}

        {videoOn ? (
          <View style={styles.videoCard}>
            <VideoAvatar
              ref={avatarRef}
              glbUrl={glbUrl}
              onError={(m) => {
                console.warn('[avatar]', m);
                setAvatarError(m);
              }}
            />
            {(ttsLoading || trackLoading) && (
              <View style={styles.voiceGenOverlay} pointerEvents="none">
                <VoicePulse />
                <Text style={styles.voiceGenText}>
                  {trackLoading ? 'Rendering audio…' : 'SyQi is preparing to speak…'}
                </Text>
              </View>
            )}
            {renderScrubber(true)}
            {renderPlay(true)}
            <View style={styles.dotsAnchorVideo}>{dots}</View>
          </View>
        ) : (
          // Video off — the stage collapses to an audio read-out bar.
          <View style={styles.audioBar}>
            {renderPlay(false)}
            <View style={styles.audioBarBody}>
              {renderScrubber(false) ?? (
                <Text style={styles.audioBarLabel}>
                  {ttsLoading ? 'Preparing…' : speaking ? 'Reading…' : 'Read out loud'}
                </Text>
              )}
            </View>
            {dots}
          </View>
        )}

        {ttsError && <Text style={styles.errorText}>Couldn't play SyQi's voice. Try again.</Text>}
        {videoOn && avatarError && <Text style={styles.errorText}>Avatar: {avatarError}</Text>}
        {banner}

        {/* ── G: the one shared text space ───────────────────────────────── */}
        {chatOpen && (
          <View style={styles.textSpace}>
            <ScrollView
              ref={scrollRef}
              contentContainerStyle={styles.textSpaceContent}
              onContentSizeChange={() => {
                if (viewKey === 'chat') scrollRef.current?.scrollToEnd({ animated: false });
              }}
              nestedScrollEnabled
            >
              {viewKey === 'chat' ? (
                <>
                  {messages.length === 0 && !sending && emptyChatText && (
                    <Text style={styles.placeholderText}>{emptyChatText}</Text>
                  )}
                  {messages.map((m, i) => (
                    <View
                      key={m.id ?? `m-${i}`}
                      style={[
                        styles.bubble,
                        m.role === 'user' ? styles.userBubble : styles.assistantBubble,
                        m.isError && styles.errorBubble,
                      ]}
                    >
                      <Text
                        style={[
                          styles.bubbleText,
                          m.role === 'user' && styles.userBubbleText,
                          m.isError && styles.errorText,
                        ]}
                      >
                        {m.content}
                      </Text>
                    </View>
                  ))}
                  {sending && (
                    <View style={[styles.bubble, styles.assistantBubble]}>
                      <Text style={styles.thinkingText}>{title} is thinking…</Text>
                    </View>
                  )}
                  {chatExtras}
                </>
              ) : (
                activeView?.render?.()
              )}
            </ScrollView>
          </View>
        )}

        {/* ── D / E / F / H: the control row ─────────────────────────────── */}
        <View style={styles.controlRow}>
          <Pressable style={styles.chevronButton} onPress={toggleChat}>
            <Text style={styles.chevronText}>{chatOpen ? '‹' : '›'}</Text>
          </Pressable>

          <Pressable style={styles.dropdown} onPress={() => setDropOpen((v) => !v)}>
            <Text style={styles.dropdownText} numberOfLines={1}>
              {activeView?.label ?? 'Chat'}
            </Text>
            <Text style={styles.dropdownCaret}>▾</Text>
          </Pressable>

          <TextInput
            style={styles.input}
            placeholder={placeholder}
            placeholderTextColor="#8a8378"
            value={input}
            onChangeText={setInput}
            onSubmitEditing={submit}
            returnKeyType="send"
          />

          <Pressable style={styles.sendButton} onPress={submit} disabled={sending}>
            <Text style={styles.sendText}>➤</Text>
          </Pressable>
        </View>

        {/* Popups last so they paint over the card. */}
        {menuOpen && (
          <>
            <Pressable style={styles.popupScrim} onPress={() => setMenuOpen(false)} />
            <View style={styles.menuPopup}>
              <Pressable style={styles.menuItem} onPress={toggleVideo}>
                <Text style={styles.menuItemText}>Video</Text>
                <Text style={styles.menuItemValue}>{videoOn ? 'On' : 'Off'}</Text>
              </Pressable>
              {menuItems?.map((item) => (
                <Pressable
                  key={item.label}
                  style={[styles.menuItem, item.disabled && styles.menuItemDisabled]}
                  disabled={item.disabled}
                  onPress={() => {
                    setMenuOpen(false);
                    item.onPress();
                  }}
                >
                  <Text style={styles.menuItemText}>{item.label}</Text>
                </Pressable>
              ))}
            </View>
          </>
        )}

        {dropOpen && (
          <>
            <Pressable style={styles.popupScrim} onPress={() => setDropOpen(false)} />
            <View style={styles.dropPopup}>
              {views.map((v) => (
                <Pressable
                  key={v.key}
                  style={[styles.menuItem, v.key === viewKey && styles.menuItemActive]}
                  onPress={() => pickView(v.key)}
                >
                  <Text style={[styles.menuItemText, v.key === viewKey && styles.menuItemTextActive]}>{v.label}</Text>
                </Pressable>
              ))}
            </View>
          </>
        )}
      </GlassCard>
    </View>
  );
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
    wrap: { gap: 8 },

    masterRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 4 },
    masterLabel: {
      fontFamily: DISPLAY_FONT,
      fontSize: 12,
      letterSpacing: 2.5,
      color: t.ivorySoft,
      textTransform: 'uppercase',
    },
    // Full-bleed band: cancels SyqiHome's 16pt container padding so the bar
    // runs edge to edge and butts flush against the shell header above it.
    masterRowNavy: {
      marginHorizontal: -16,
      marginTop: -16,
      paddingHorizontal: 20,
      paddingVertical: 10,
      backgroundColor: t.glassStrong,
      borderBottomWidth: 1,
      borderBottomColor: t.navyLine,
    },
    masterLabelNavy: { color: t.ivory },

    // Glass frame around the whole module.
    card: {
      borderRadius: radii.panel,
      padding: 12,
      gap: 10,
      overflow: 'hidden',
    },

    // The stage stays dark in both modes — it hosts the avatar scene.
    videoCard: {
      height: 220,
      borderRadius: radii.tile,
      backgroundColor: t.night,
      borderWidth: 1,
      borderColor: t.navyLine,
      overflow: 'hidden',
    },

    audioBar: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      borderRadius: radii.tile,
      backgroundColor: t.glass,
      borderWidth: 1,
      borderColor: t.navyLine,
      paddingHorizontal: 10,
      paddingVertical: 10,
    },
    audioBarBody: { flex: 1, justifyContent: 'center', minHeight: 24 },
    audioBarLabel: { fontSize: 13, fontWeight: '700', color: t.goldBright },

    // A — bottom LEFT of the stage, never the right.
    playButton: {
      width: 44,
      height: 44,
      borderRadius: 22,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: t.gold,
      boxShadow: `0 6px 18px ${t.goldGlow}`,
    },
    playButtonFloating: { position: 'absolute', bottom: 12, left: 12 },
    playButtonInline: { width: 40, height: 40, borderRadius: 20 },
    playButtonText: { color: t.onGold, fontSize: 16, fontWeight: '900' },

    // B — the ⋮ affordance, top right of the stage.
    dotsAnchorVideo: { position: 'absolute', top: 8, right: 8 },
    dotsButton: { width: 28, alignItems: 'center', justifyContent: 'center', paddingVertical: 6, gap: 4 },
    dot: { width: 5, height: 5, borderRadius: 3, backgroundColor: t.goldBright },
    // Over the avatar scene: always light, whatever the mode.
    dotOnVideo: { width: 5, height: 5, borderRadius: 3, backgroundColor: '#ffffff' },

    trackRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    // Over the stage the scrubber clears the floating play button (left: 12 +
    // 44 wide) and rides its own scrim — the avatar's sky backdrop is bright,
    // so plain white text on it is unreadable. Inline it just fills the
    // read-out bar's middle cell.
    trackRowFloating: {
      position: 'absolute',
      left: 66,
      right: 12,
      bottom: 20,
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: radii.pill,
      backgroundColor: 'rgba(7, 11, 23, 0.6)',
    },
    trackRowInline: { flex: 1 },
    trackTime: { fontSize: 10, fontWeight: '600', color: 'rgba(255,255,255,0.9)', fontVariant: ['tabular-nums'] },
    trackTimeDark: { color: t.ivorySoft },
    trackBar: { flex: 1, height: 22, justifyContent: 'center' },
    trackRail: { height: 4, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.35)', overflow: 'hidden' },
    trackRailDark: { backgroundColor: t.track },
    trackFill: { height: '100%', backgroundColor: t.gold },

    voiceGenOverlay: {
      ...StyleSheet.absoluteFillObject,
      alignItems: 'center',
      justifyContent: 'center',
      gap: 10,
      backgroundColor: 'rgba(7, 11, 23, 0.55)',
    },
    voiceGenText: { fontSize: 13, fontWeight: '600', color: '#ffffff' },
    voicePulseRow: { flexDirection: 'row', alignItems: 'center', gap: 5, height: 28 },
    voicePulseBar: { width: 5, borderRadius: 3, backgroundColor: t.gold },

    // G — the single shared text space.
    textSpace: {
      height: 210,
      borderRadius: radii.tile,
      backgroundColor: 'rgba(0,0,0,0.18)',
      borderWidth: 1,
      borderColor: t.lineFaint,
    },
    textSpaceContent: { padding: 12, gap: 10 },
    placeholderText: { color: t.ivoryFaint, fontStyle: 'italic', textAlign: 'center', marginTop: 24 },

    bubble: { maxWidth: '86%', borderRadius: 18, paddingHorizontal: 14, paddingVertical: 10, borderWidth: 1 },
    userBubble: { alignSelf: 'flex-end', backgroundColor: t.gold, borderColor: t.gold },
    assistantBubble: { alignSelf: 'flex-start', backgroundColor: t.navyRaised, borderColor: t.navyLine },
    errorBubble: { borderColor: t.lapsed },
    bubbleText: { fontSize: 14, lineHeight: 20, color: t.ivory },
    userBubbleText: { color: t.onGold },
    thinkingText: { color: t.ivorySoft, fontStyle: 'italic', fontSize: 13 },
    errorText: { fontSize: 12, color: t.lapsed, fontStyle: 'italic' },

    controlRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    // D — the accordion chevron.
    chevronButton: {
      width: 34,
      height: 34,
      borderRadius: 17,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: t.navyLine,
      backgroundColor: t.navyRaised,
    },
    chevronText: { fontSize: 18, fontWeight: '900', color: t.goldBright, lineHeight: 20 },
    // E — the view dropdown.
    dropdown: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      maxWidth: 130,
      borderRadius: radii.pill,
      borderWidth: 1,
      borderColor: t.navyLine,
      backgroundColor: t.navyRaised,
      paddingHorizontal: 12,
      paddingVertical: 8,
    },
    dropdownText: { flexShrink: 1, fontSize: 11, fontWeight: '800', letterSpacing: 0.4, color: t.goldBright },
    dropdownCaret: { fontSize: 10, fontWeight: '900', color: t.goldBright },
    // F — the input (same recipe as the deity sheet: white on dark, gold rim).
    input: {
      flex: 1,
      minWidth: 60,
      height: 38,
      backgroundColor: '#ffffff',
      borderRadius: 19,
      paddingHorizontal: 14,
      fontSize: 14,
      color: '#111',
      borderWidth: 1,
      borderColor: t.gold,
    },
    // H — send.
    sendButton: {
      boxShadow: `0 4px 14px ${t.goldGlow}`,
      width: 38,
      height: 38,
      borderRadius: 19,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: t.gold,
    },
    sendText: { color: t.onGold, fontSize: 16, fontWeight: '900' },

    popupScrim: { ...StyleSheet.absoluteFillObject },
    menuPopup: {
      position: 'absolute',
      top: 44,
      right: 20,
      minWidth: 170,
      borderRadius: radii.tile,
      backgroundColor: t.glassStrong,
      borderWidth: 1,
      borderColor: t.navyLine,
      paddingVertical: 4,
      boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
    },
    dropPopup: {
      position: 'absolute',
      left: 54,
      bottom: 56,
      minWidth: 180,
      borderRadius: radii.tile,
      backgroundColor: t.glassStrong,
      borderWidth: 1,
      borderColor: t.navyLine,
      paddingVertical: 4,
      boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
    },
    menuItem: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      paddingHorizontal: 14,
      paddingVertical: 10,
    },
    menuItemDisabled: { opacity: 0.4 },
    menuItemActive: { backgroundColor: t.lineFaint },
    menuItemText: { fontSize: 13, fontWeight: '700', color: t.ivory },
    menuItemTextActive: { color: t.goldBright },
    menuItemValue: { fontSize: 12, fontWeight: '800', color: t.goldBright },
  }),
);

export default AiModule;
